
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Clock, Users, Mail, Loader2 } from 'lucide-react';
import { useJobOffers } from '@/hooks/useJobOffers';

const JobOffers = () => {
  const { data: jobs, isLoading, error } = useJobOffers();

  if (isLoading) {
    return (
      <section id="job-offers" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6 text-fabre-gray">
              Nos offres d'emploi
            </h2>
            <p className="text-lg text-gray-600">
              Découvrez les postes disponibles et postulez dès maintenant
            </p>
          </div>
          <div className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-fabre-green" />
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="job-offers" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6 text-fabre-gray">
              Nos offres d'emploi
            </h2>
            <p className="text-lg text-red-600">
              Erreur lors du chargement des offres d'emploi. Veuillez réessayer plus tard.
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (!jobs || jobs.length === 0) {
    return (
      <section id="job-offers" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6 text-fabre-gray">
              Nos offres d'emploi
            </h2>
            <p className="text-lg text-gray-600">
              Aucune offre d'emploi n'est disponible pour le moment.
            </p>
            <p className="text-gray-600 mt-4">
              N'hésitez pas à nous envoyer une candidature spontanée !
            </p>
            <Button 
              className="mt-6 bg-fabre-green hover:bg-fabre-darkgreen"
              onClick={() => document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Candidature spontanée
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="job-offers" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6 text-fabre-gray">
            Nos offres d'emploi
          </h2>
          <p className="text-lg text-gray-600">
            Découvrez les postes disponibles et postulez dès maintenant
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {jobs.map((job) => (
            <Card key={job.id} className="hover:shadow-lg transition-shadow duration-300">
              <CardHeader>
                <div className="flex justify-between items-start mb-2">
                  <CardTitle className="text-xl text-fabre-gray">{job.title}</CardTitle>
                  <Badge variant="outline" className="text-fabre-green border-fabre-green">
                    {job.department}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {job.location}
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {job.contract_type}
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {job.experience_level}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">{job.description}</p>
                
                <div className="mb-4">
                  <h4 className="font-semibold text-fabre-gray mb-2">Profil recherché :</h4>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    {job.requirements.map((req, idx) => (
                      <li key={idx}>{req}</li>
                    ))}
                  </ul>
                </div>

                <Button 
                  className="w-full bg-fabre-green hover:bg-fabre-darkgreen"
                  onClick={() => {
                    const form = document.getElementById('application-form');
                    if (form) {
                      form.scrollIntoView({ behavior: 'smooth' });
                      // Pré-remplir le poste dans le formulaire
                      const jobField = document.getElementById('job-position') as HTMLSelectElement;
                      if (jobField) {
                        jobField.value = job.title;
                      }
                    }
                  }}
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Postuler
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">
            Vous ne trouvez pas le poste qui vous correspond ?
          </p>
          <Button 
            variant="outline" 
            className="border-fabre-green text-fabre-green hover:bg-fabre-lightgreen"
            onClick={() => document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Candidature spontanée
          </Button>
        </div>
      </div>
    </section>
  );
};

export default JobOffers;
